// components/ManagementAccounting.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import HorizontalImagesStrip from "../ones/HorizontalImagesStrip";

import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';

import {
  IoMdCheckmarkCircleOutline,
  IoMdTrendingUp,
  IoMdDocument,
  IoMdGlobe,
  IoMdArrowDropright,
  IoMdCalculator,
  IoMdPeople,
  IoMdAnalytics
} from "react-icons/io";
import { FaChartLine, FaBalanceScale, FaBullseye } from "react-icons/fa";
import { FaShield } from "react-icons/fa6";

export default function ManagementAccounting(props) {

  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  
  // Banner rotation for hero section
  const banners = [banner1, banner2, banner3, banner4, banner1, banner3];

  // Management Accounting Service Pillars - ATRIC Profile Aligned
  const servicePillars = [
    {
      id: 1,
      title: "Strategy Review & Alignment",
      icon: <FaBullseye />,
      description: "Comprehensive review of business, financial, and risk strategies using management accounting tools to align organizational objectives with executable plans.",
      features: [
        "Business Model Analysis (BGG Matrix, Porter's Value Chain)",
        "Strategic Option Evaluation (SAF Framework)",
        "Digital Strategy & Process Re-engineering Advisory"
      ],
      gradient: "from-blue-500/20 to-indigo-500/10",
      accent: "#60a5fa"
    },
    {
      id: 2,
      title: "Performance Management",
      icon: <IoMdAnalytics />,
      description: "Implementation of balanced scorecards, KPI frameworks, and change management strategies to synchronize employee performance with organizational vision.",
      features: [
        "Balanced Scorecard Development (4 Perspectives)",
        "KPI Alignment with Vision, Mission & Core Values",
        "Change Management Strategy & Stakeholder Engagement"
      ],
      gradient: "from-emerald-500/20 to-teal-500/10",
      accent: "#10b981"
    },
    {
      id: 3,
      title: "Financial Strategy & Control",
      icon: <FaChartLine />,
      description: "Strategic financial planning, capital allocation, and reporting frameworks that support sustainable growth while ensuring regulatory compliance.",
      features: [
        "Capital Structure Optimization & Investment Appraisal",
        "Cash Flow Management & Treasury Advisory",
        "IFRS/IPSAS Compliance & ESG Reporting Integration"
      ],
      gradient: "from-violet-500/20 to-purple-500/10",
      accent: "#a78bfa"
    },
    {
      id: 4,
      title: "Risk Management & Controls",
      icon: <FaShield/>,
      description: "Robust risk identification, assessment, and mitigation frameworks including cyber risk management to protect organizational value and ensure continuity.",
      features: [
        "CGMA Risk Management Cycle Implementation",
        "Cyber Risk Dashboard & Control Frameworks",
        "Departmental Risk Registers & Control Testing"
      ],
      gradient: "from-amber-500/20 to-orange-500/10",
      accent: "#f59e0b"
    }
  ];

  // Management Accounting Practice Areas (14 Global Principles)
  const practiceAreas = [
    { name: "Cost Transformation", icon: "💡" },
    { name: "External Reporting", icon: "📊" },
    { name: "Financial Strategy", icon: "🎯" },
    { name: "Internal Controls", icon: "🔒" },
    { name: "Investment Appraisal", icon: "📈" },
    { name: "Budgetary Control", icon: "📋" },
    { name: "Pricing Strategy", icon: "💰" },
    { name: "Project Management", icon: "🚀" },
    { name: "Regulatory Compliance", icon: "⚖️" },
    { name: "Resource Management", icon: "👥" },
    { name: "Risk Management", icon: "🛡️" },
    { name: "Strategic Tax", icon: "🧾" },
    { name: "Treasury & Cash", icon: "💵" },
    { name: "Internal Audit", icon: "🔍" }
  ];

  async function init() {
    setReady(false);
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!ready) return;
    const interval = setInterval(() => {
      setBgIndex(prev => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [ready]);

  if (!ready) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      {/* ================= HERO SECTION ================= */}
      <section className="MainBodySection position-relative">
        <div style={{
          width: "100%",
          position: "relative",
          overflow: "hidden"
        }}>
          {/* Rotating Banner Backgrounds */}
          {banners.map((banner, i) => (
            <div
              key={i}
              style={{
                backgroundImage: `url(${banner})`,
                backgroundAttachment: "fixed",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                transition: "opacity 2s ease-in-out",
                opacity: i === bgIndex ? 1 : 0,
                zIndex: 0
              }}
              aria-hidden={i !== bgIndex}
            />
          ))}

          {/* Dark Overlay with Content */}
          <div style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 50%, rgba(15,23,42,0.7) 100%)",
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            backdropFilter: "blur(2px)"
          }}>
            <div className="container py-5">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <span className="d-inline-block px-3 py-1 mb-3 rounded-pill" 
                    style={{ 
                      background: "rgba(59,130,246,0.15)", 
                      border: "1px solid rgba(59,130,246,0.3)", 
                      color: "#60a5fa", 
                      fontSize: "13px", 
                      fontWeight: "500" 
                    }}>
                    Management Accounting Excellence
                  </span>
                  
                  <h1 className="text-light mb-3" style={{
                    fontSize: "clamp(2rem, 4vw, 3.2rem)",
                    fontWeight: "700",
                    lineHeight: "1.15",
                    letterSpacing: "-0.5px"
                  }}>
                    Transform Data into Strategic Decisions
                  </h1>

                  <p className="text-light mt-3 mb-4" style={{
                    fontSize: "1.1rem",
                    opacity: 0.9,
                    lineHeight: "1.7",
                    maxWidth: "650px"
                  }}>
                    ATRIC applies global management accounting principles—Influence, Relevance, Analysis, and Trust—to help organizations connect strategy with execution, drive accountability, and preserve long-term value.
                  </p>

                  <div className="d-flex flex-wrap gap-3">
                    <button
                      className="btn btn-primary btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                      onClick={() => appContext.navTo({ item: 'contact_us', service: 'Management Accounting' })}
                      style={{ 
                        background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                        border: "none",
                        fontWeight: "500",
                        boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)"
                      }}
                    >
                      Request Consultation
                      <IoMdArrowDropright size={20} />
                    </button>
                    <a 
                      href="#practice-areas" 
                      className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill"
                      style={{ 
                        borderColor: "rgba(255,255,255,0.3)",
                        fontWeight: "500"
                      }}
                    >
                      Explore Practice Areas
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= VALUE PROPOSITION ================= */}
      <section style={{
        background: "linear-gradient(to bottom, #0f172a 0%, #1e293b 100%)",
        padding: "70px 0"
      }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-9">
              <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2rem" }}>
                Why Management Accounting Matters
              </h2>
              <p className="text-light opacity-75 mb-4" style={{ lineHeight: "1.7", fontSize: "1.05rem" }}>
                Management Accounting offers a structured approach to organizational management that supports strategy development and implementation to create and preserve value. By identifying relevant information, conducting thorough analysis, and communicating insights effectively, we enable better decision-making and stewardship.
              </p>
              
              {/* Core Principles Badges */}
              <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                {[
                  { name: "Influence", desc: "Drive integrated thinking" },
                  { name: "Relevance", desc: "Identify timely information" },
                  { name: "Analysis", desc: "Connect strategy to value" },
                  { name: "Trust", desc: "Ensure accountability" }
                ].map((principle, idx) => (
                  <div key={idx} className="badge rounded-pill px-3 py-2 d-flex align-items-center gap-2" 
                    style={{ 
                      background: "rgba(255,255,255,0.08)", 
                      border: "1px solid rgba(255,255,255,0.15)",
                      color: "#e2e8f0",
                      fontSize: "0.85rem",
                      fontWeight: "500"
                    }}>
                    <IoMdCheckmarkCircleOutline size={14} style={{ color: "#60a5fa" }} />
                    <span>{principle.name}</span>
                  </div>
                ))}
              </div>

              <HorizontalImagesStrip
                images={[banner1, banner2, banner3, banner4]}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICE PILLARS GRID ================= */}
      <section style={{
        background: "#0f172a",
        padding: "80px 0"
      }} id="services">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2.2rem" }}>
              Our Management Accounting Framework
            </h2>
            <p className="text-light opacity-75" style={{ maxWidth: "700px", margin: "0 auto", lineHeight: "1.6" }}>
              Four integrated pillars delivering strategic financial intelligence, operational excellence, and sustainable value creation.
            </p>
          </div>

          <div className="row g-4">
            {servicePillars.map((pillar) => (
              <div className="col-md-6 col-lg-3" key={pillar.id}>
                <div 
                  className="h-100 p-4 rounded-3 border position-relative overflow-hidden"
                  style={{
                    background: `linear-gradient(145deg, rgba(30,41,59,0.8), rgba(15,23,42,0.9))`,
                    backdropFilter: "blur(12px)",
                    borderColor: "rgba(255,255,255,0.08)",
                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = "0 20px 40px -15px rgba(0, 0, 0, 0.4)";
                    e.currentTarget.style.borderColor = pillar.accent + "66";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Learn more about ${pillar.title}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      appContext.navTo({ item: 'contact_us', service: pillar.title });
                    }
                  }}
                >
                  {/* Decorative gradient accent */}
                  <div className="position-absolute top-0 end-0 p-3 opacity-20" 
                    style={{ 
                      background: `linear-gradient(135deg, ${pillar.gradient.split(' ')[0].replace('/20','/40')}, ${pillar.gradient.split(' ')[1].replace('/10','/25')})`,
                      width: "80px",
                      height: "80px",
                      borderRadius: "0 0 0 100%",
                      zIndex: 0
                    }} 
                  />

                  {/* Icon Badge */}
                  <div className="d-inline-flex align-items-center justify-content-center rounded-2 p-2 mb-3"
                    style={{
                      background: `linear-gradient(135deg, ${pillar.gradient})`,
                      border: `1px solid rgba(255,255,255,0.15)`,
                      color: "#fff",
                      width: "48px",
                      height: "48px",
                      fontSize: "1.3rem",
                      zIndex: 1
                    }}>
                    {pillar.icon}
                  </div>

                  {/* Service Content */}
                  <h5 className="text-light mb-2" style={{ fontWeight: "600", fontSize: "1.25rem", zIndex: 1 }}>
                    {pillar.title}
                  </h5>
                  <p className="text-light opacity-80 mb-3 small" style={{ lineHeight: "1.6", zIndex: 1 }}>
                    {pillar.description}
                  </p>

                  {/* Feature List */}
                  <ul className="list-unstyled mb-4" style={{ zIndex: 1 }}>
                    {pillar.features.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start gap-2 mb-2 small">
                        <IoMdCheckmarkCircleOutline 
                          className="flex-shrink-0 mt-0.5" 
                          size={16} 
                          style={{ color: pillar.accent }} 
                        />
                        <span className="text-light opacity-85" style={{ fontSize: "0.9rem" }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Link */}
                  <a 
                    href="#contact"
                    className="d-inline-flex align-items-center gap-1 text-decoration-none"
                    style={{ 
                      color: pillar.accent, 
                      fontWeight: "500", 
                      fontSize: "0.9rem",
                      zIndex: 1,
                      transition: "gap 0.2s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.gap = "6px"}
                    onMouseLeave={(e) => e.currentTarget.style.gap = "4px"}
                    onClick={(e) => {
                      e.preventDefault();
                      appContext.navTo({ item: 'contact_us', service: pillar.title });
                    }}
                  >
                    Learn More
                    <IoMdArrowDropright size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PRACTICE AREAS ================= */}
      <section id="practice-areas" style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        padding: "75px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)"
      }}>
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              14 Global Management Accounting Practice Areas
            </h3>
            <p className="text-light opacity-75">Based on AICPA & CIMA Global Principles for effective finance functions</p>
          </div>

          <div className="row g-3 justify-content-center">
            {practiceAreas.map((area, idx) => (
              <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={idx}>
                <div 
                  className="p-3 rounded-2 text-center"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    transition: "all 0.2s ease",
                    cursor: "default"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div className="mb-2" style={{ fontSize: "1.4rem" }}>{area.icon}</div>
                  <span className="text-light small" style={{ fontWeight: "500", fontSize: "0.85rem" }}>
                    {area.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= METHODOLOGY ================= */}
      <section style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        padding: "75px 0"
      }}>
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              Our Engagement Methodology
            </h3>
            <p className="text-light opacity-75">A proven cycle for sustainable performance improvement</p>
          </div>

          <div className="row g-4 justify-content-center">
            {[
              { 
                step: "01", 
                title: "Assess", 
                desc: "Diagnostic review of current management accounting function against global principles and practice areas",
                icon: <IoMdAnalytics size={24} />
              },
              { 
                step: "02", 
                title: "Design", 
                desc: "Tailored framework aligning people, principles, performance systems, and practice areas to your strategy",
                icon: <IoMdDocument size={24} />
              },
              { 
                step: "03", 
                title: "Implement", 
                desc: "Phased rollout with change management, capability building, and clear accountability structures",
                icon: <IoMdPeople size={24} />
              },
              { 
                step: "04", 
                title: "Sustain", 
                desc: "Continuous monitoring, reporting, and refinement to embed a culture of value-driven decision-making",
                icon: <IoMdTrendingUp size={24} />
              }
            ].map((item, idx) => (
              <div className="col-md-6 col-lg-3" key={idx}>
                <div className="text-center p-4 h-100" style={{ position: "relative" }}>
                  {/* Connector line for desktop */}
                  {idx < 3 && (
                    <div className="d-none d-lg-block position-absolute top-50 end-0" 
                      style={{ 
                        width: "100%", 
                        height: "1px", 
                        background: "linear-gradient(to right, rgba(96,165,250,0.3), transparent)",
                        transform: "translateY(-50%) translateX(50%)",
                        zIndex: 0
                      }} 
                    />
                  )}
                  
                  <div className="position-relative z-1">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto"
                      style={{
                        width: "56px",
                        height: "56px",
                        background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                        color: "white",
                        fontWeight: "700",
                        fontSize: "1.1rem",
                        boxShadow: "0 8px 20px -5px rgba(59, 130, 246, 0.4)"
                      }}>
                      {item.icon}
                    </div>
                    <div className="badge bg-primary bg-opacity-10 text-primary mb-2" style={{ fontSize: "0.8rem", fontWeight: "600" }}>
                      Step {item.step}
                    </div>
                    <h6 className="text-light mb-2" style={{ fontWeight: "600" }}>{item.title}</h6>
                    <p className="text-light opacity-75 small mb-0" style={{ lineHeight: "1.6" }}>{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section id="contact" style={{
        background: "linear-gradient(135deg, #0c1424 0%, #1e293b 100%)",
        padding: "65px 0",
        borderTop: "1px solid rgba(255,255,255,0.08)"
      }}>
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <IoMdCalculator size={42} className="text-primary mb-3" />
              <h3 className="text-light mb-3" style={{ fontWeight: "600" }}>
                Ready to Strengthen Your Financial Decision-Making?
              </h3>
              <p className="text-light opacity-80 mb-4" style={{ lineHeight: "1.7" }}>
                Our experienced management accountants bring CGMA competency, sector expertise, and global best practices to help you connect strategy with execution and preserve long-term value.
              </p>
              
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <button
                  className="btn btn-primary btn-lg px-5 py-2 rounded-pill"
                  onClick={() => appContext.navTo({ item: 'contact_us', service: 'Management Accounting' })}
                  style={{ 
                    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                    border: "none",
                    fontWeight: "600",
                    boxShadow: "0 6px 20px rgba(59, 130, 246, 0.3)"
                  }}
                >
                  Schedule a Discovery Call
                </button>
                <a 
                  href="tel:+255717918808"
                  className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                  style={{ borderColor: "rgba(255,255,255,0.25)" }}
                >
                  <span style={{ fontSize: "1.1rem" }}>📞</span>
                  +255 717 918 808
                </a>
              </div>
              
              <p className="text-light opacity-60 small mt-4 mb-0">
                📍 2nd Floor, The Tax Hub Building, Industrial Road, Mikocheni B, Dar es Salaam
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}